import { findAtDepthZero, findMatchingCloser, findDocSummary } from './CsTokenScanHelpers';

const TYPE_MODIFIERS = new Set(['public', 'private', 'protected', 'internal', 'static', 'sealed', 'abstract', 'partial', 'unsafe', 'new']);

// Walks back from a class/interface keyword over any preceding modifier
// tokens (public, abstract, partial, ...) to the index a doc-comment would be
// attached at — docComments store the token-index of the first real token
// after the `///` block, i.e. the first modifier, or the keyword itself if
// there are no modifiers.
function findDeclStartForDocs(tokens, keywordIndex) {
  let start = keywordIndex;
  while (start > 0 && tokens[start - 1].type === 'identifier' && TYPE_MODIFIERS.has(tokens[start - 1].value)) {
    start -= 1;
  }
  return start;
}

// Splits a base/interface list (`: CoreBase2, IDslHelper`) on top-level commas
// (depth-aware, so generic base types like `List<T>` don't split in half),
// then takes just the first identifier per piece as the base type's name —
// enough to look it up among other types parsed from the SAME paste (see
// CsClassParser's inheritMemberDescriptions). Does not resolve base types
// outside the pasted source (e.g. CoreBase2 here) — those are just names
// with nothing to match against, silently skipped downstream.
function parseBaseList(tokens, colonIndex, bodyOpenIndex) {
  const listTokens = tokens.slice(colonIndex + 1, bodyOpenIndex);
  const pieces = [];
  let depth = 0;
  let start = 0;
  for (let i = 0; i <= listTokens.length; i += 1) {
    const isEnd = i === listTokens.length;
    const value = isEnd ? null : listTokens[i].value;
    if (!isEnd && (value === '<' || value === '(')) depth += 1;
    if (!isEnd && (value === '>' || value === ')')) depth -= 1;
    if (isEnd || (depth === 0 && value === ',')) {
      if (i > start) pieces.push(listTokens.slice(start, i));
      start = i + 1;
    }
  }
  return pieces
    .map(function firstIdentifierName(piece) {
      const first = piece.find(function isIdentifier(t) { return t.type === 'identifier'; });
      return first ? first.value : null;
    })
    .filter(Boolean);
}

/**
 * Finds all class/interface declarations in a token stream — token-based,
 * so generic parameter lists, base lists and where-clauses don't need their
 * own regex; they're just skipped structurally on the way to the body `{`.
 * Nested types inside a body are not recursed into separately (same scope
 * as the previous regex-based splitter).
 * @param {{type: string, value: string}[]} tokens
 * @param {{tokenIndex: number, summary: string}[]} docComments - from tokenizeCs, XMLDoc summaries
 * @returns {{name: string, kind: 'class'|'interface', isAbstract: boolean, bodyTokens: object[], bodyOffset: number, description: string|null}[]}
 */
export function scanCsTypes(tokens, docComments = []) {
  const types = [];
  let i = 0;

  while (i < tokens.length) {
    const token = tokens[i];
    const isTypeKeyword = token.type === 'identifier' && (token.value === 'class' || token.value === 'interface');
    if (!isTypeKeyword) {
      i += 1;
      continue;
    }

    const kind = token.value;
    let isAbstract = false;
    for (let b = i - 1; b >= 0 && i - b <= 6; b -= 1) {
      const prev = tokens[b];
      if (prev.type === 'punctuation' && ['{', '}', ';'].includes(prev.value)) break;
      if (prev.type === 'identifier' && prev.value === 'abstract') isAbstract = true;
    }

    let j = i + 1;
    if (!tokens[j] || tokens[j].type !== 'identifier') {
      i += 1;
      continue;
    }
    const name = tokens[j].value;
    j += 1;

    if (tokens[j] && tokens[j].value === '<') {
      j = findMatchingCloser(tokens, j) + 1;
    }

    let inherits = [];
    let bodyOpenIndex;
    if (tokens[j] && tokens[j].value === ':') {
      const colonIndex = j;
      bodyOpenIndex = findAtDepthZero(tokens, colonIndex + 1, ['{']);
      inherits = parseBaseList(tokens, colonIndex, bodyOpenIndex);
    } else {
      bodyOpenIndex = findAtDepthZero(tokens, j, ['{']);
    }

    if (bodyOpenIndex >= tokens.length) {
      i = j;
      continue;
    }
    const bodyCloseIndex = findMatchingCloser(tokens, bodyOpenIndex);
    const bodyTokens = tokens.slice(bodyOpenIndex + 1, bodyCloseIndex);

    const description = findDocSummary(docComments, findDeclStartForDocs(tokens, i));

    types.push({ name, kind, isAbstract, bodyTokens, bodyOffset: bodyOpenIndex + 1, description, inherits });
    i = bodyCloseIndex + 1;
  }

  return types;
}
